import { AppVariant, AppVariantConfig, AppModuleKey, AdminModuleKey } from '@/types/app-config';

/**
 * Determines the best app variant based on enabled features
 */
export function determineAppVariant(features: Record<string, boolean>): AppVariant {
  // Logic to determine the best variant based on enabled features
  const hasMarketplace = features['payments.marketplace'];
  const hasDonations = features['payments.donations'];
  const hasGroups = features['groups.blog'];
  const hasEvents = features['events'];
  const hasChannels = features['channels'];
  const hasMail = features['mail'];
  const hasAddressBook = features['address_book'];
  const hasArchive = features['archive'];
  
  const portalFeatures = [hasChannels, hasEvents, hasMail, hasAddressBook].filter(Boolean).length;
  const totalFeatures = Object.values(features).filter(Boolean).length;
  
  // Decision logic for new variants
  
  // Organizations: Full suite - marketplace + groups + multiple portal features
  if (hasMarketplace && hasGroups && portalFeatures >= 3 && totalFeatures >= 6) {
    return 'organizations';
  }
  
  // Marketplaces: E-commerce focused with both marketplace and donations
  if (hasMarketplace && hasDonations) {
    return 'marketplaces';
  }
  
  // Marketplace Only: Pure e-commerce focused with marketplace only
  if (hasMarketplace && !hasDonations) {
    return 'marketplace-only';
  }
  
  // Churches: Groups + events + donations, but no marketplace
  if (hasGroups && hasEvents && hasDonations && !hasMarketplace) {
    return 'churches';
  }
  
  // Teams: Groups + communication tools (mail/channels) but no marketplace/donations
  if (hasGroups && (hasMail || hasChannels) && !hasMarketplace && !hasDonations) {
    return 'teams';
  }
  
  // CRMs: Focus on contacts and communication without groups
  if ((hasMail || hasAddressBook) && hasArchive && !hasGroups && !hasMarketplace) {
    return 'crms';
  }
  
  // Default fallback logic
  if (hasGroups || hasEvents) {
    return 'teams'; // Default for collaboration features
  }
  
  if (hasMail || hasAddressBook) {
    return 'crms'; // Default for contact management
  }
  
  // If we have marketplace but no other distinguishing features, default to marketplace-only
  if (hasMarketplace) {
    return 'marketplace-only';
  }
  
  // Ultimate fallback
  return 'organizations';
}

/**
 * Gets enabled app modules for a variant
 */
export function getEnabledAppModules(variant: AppVariantConfig): AppModuleKey[] {
  return Object.keys(variant.appModules || {}) as AppModuleKey[];
}

/**
 * Gets enabled admin modules for a variant
 */
export function getEnabledAdminModules(variant: AppVariantConfig): AdminModuleKey[] {
  return Object.keys(variant.adminModules || {}) as AdminModuleKey[];
}

/**
 * Builds a custom variant configuration from project features
 */
export function buildCustomVariant(
  features: Record<string, boolean>,
  customConfig?: Partial<AppVariantConfig>
): AppVariantConfig {
  const appModules: Partial<Record<AppModuleKey, any>> = {
    directory: { enabled: true }, // Always include directory
  };
  
  const adminModules: Partial<Record<AdminModuleKey, any>> = {
    users: { enabled: true }, // Always include user management
    settings: { enabled: true }, // Always include settings
  };

  // Add modules based on features
  if (features.home) {
    appModules.home = { enabled: true, requiresFeature: 'home' };
  }
  
  if (features['payments.marketplace']) {
    appModules.marketplace = { 
      enabled: true, 
      requiresFeature: 'payments.marketplace',
      checkIfLive: true 
    };
    adminModules.payments = { enabled: true };
  }
  
  if (features['payments.donations']) {
    appModules.payments = { 
      enabled: true, 
      requiresFeature: 'payments',
      subModules: {
        donations: { enabled: true, requiresFeature: 'payments.donations', checkIfLive: true }
      }
    };
  }
  
  if (features['groups.blog']) {
    appModules.groups = { enabled: true, requiresFeature: 'groups.blog' };
    adminModules.groups = { enabled: true };
  }
  
  if (features.archive) {
    appModules.archive = { enabled: true, requiresFeature: 'archive' };
    adminModules.archive = { enabled: true };
  }

  // Portal features - align with features store logic
  const portalSubModules: any = {};
  if (features.channels) {
    portalSubModules.channels = { enabled: true, requiresFeature: 'channels' };
    adminModules.channels = { enabled: true };
  }
  if (features.events) {
    portalSubModules.events = { enabled: true, requiresFeature: 'events' };
    adminModules.events = { enabled: true };
  }
  if (features.mail) {
    portalSubModules.mail = { enabled: true, requiresFeature: 'mail' };
    adminModules.mail = { enabled: true };
  }
  if (features.address_book) {
    portalSubModules.addressBook = { enabled: true, requiresFeature: 'address_book' };
    adminModules.address_book = { enabled: true };
  }
  
  // Enable portal if any portal subfeatures are available (matches features store logic)
  const hasPortalFeatures = features.channels || features.events || features.mail || features.address_book;
  if (hasPortalFeatures || Object.keys(portalSubModules).length > 0) {
    appModules.portal = { 
      enabled: true, 
      requiresFeature: 'portal',
      subModules: portalSubModules
    };
  }

  return {
    id: 'custom',
    name: 'Custom',
    description: 'Dynamically generated from project features',
    defaultRoute: customConfig?.defaultRoute || 'directory',
    appModules,
    adminModules,
    ...customConfig
  };
}

/**
 * Validates a variant configuration
 */
export function validateVariantConfig(config: AppVariantConfig): string[] {
  const errors: string[] = [];
  
  if (!config.id) {
    errors.push('Variant must have an id');
  }
  
  if (!config.name) {
    errors.push('Variant must have a name');
  }
  
  if (!config.defaultRoute) {
    errors.push('Variant must have a defaultRoute');
  }
  
  // Check that at least directory or some app module is enabled
  const hasEnabledAppModules = Object.values(config.appModules || {}).some(module => module.enabled);
  if (!hasEnabledAppModules) {
    errors.push('Variant must have at least one enabled app module');
  }
  
  // Check that at least users or settings admin module is enabled
  const hasEnabledAdminModules = Object.values(config.adminModules || {}).some(module => module.enabled);
  if (!hasEnabledAdminModules) {
    errors.push('Variant must have at least one enabled admin module');
  }
  
  return errors;
}

/**
 * Merges two variant configurations (useful for overrides)
 */
export function mergeVariantConfigs(
  base: AppVariantConfig, 
  override: Partial<AppVariantConfig>
): AppVariantConfig {
  return {
    ...base,
    ...override,
    appModules: {
      ...base.appModules,
      ...override.appModules
    },
    adminModules: {
      ...base.adminModules,
      ...override.adminModules
    }
  };
} 